// src/pages/admin/analytics/components/AddWastageModal.jsx
import { useState, useEffect, useMemo } from 'react';
import { X, Package, Trash2, Plus, AlertCircle, ShoppingBag, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import analyticsService from '../../../../services/analyticsService';
import rawMaterialsService from '../../../../services/rawMaterialsService';
import ProductService from '../../../../services/productService';
import ProductInventoryService from '../../../../services/productInventoryService';
import RawMaterialInventoryService from '../../../../services/rawMaterialInventoryService';
import SearchableSelect from '../../../../components/common/SearchableSelect';

const WASTAGE_REASONS = [
  { value: 'trimming_prep', label: 'Preparation / Trimming Loss (e.g. Chicken cleaning, peeling)' },
  { value: 'spillage_damage', label: 'Spillage / Accidental Damage' },
  { value: 'expired_spoilage', label: 'Product Expired / Spoiled' },
  { value: 'quality_defect', label: 'Quality Defect / Customer Complaint' },
  { value: 'prep_error', label: 'Preparation Error / Kitchen Mistake' },
];

const AddWastageModal = ({ isOpen, onClose, onSuccess, branches = [], currentBranch = '' }) => {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');

  // Branch live stock maps
  const [branchProductStockMap, setBranchProductStockMap] = useState({});
  const [branchRawMaterialStockMap, setBranchRawMaterialStockMap] = useState({});

  // Current draft item form state
  const [itemType, setItemType] = useState('raw_material'); // 'raw_material' | 'product'
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitName, setUnitName] = useState('units');
  const [reason, setReason] = useState('trimming_prep');
  const [notes, setNotes] = useState('');

  // Multi-item Queue List state
  const [wastageList, setWastageList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [fetchingItems, setFetchingItems] = useState(false);

  // Fetch Raw Materials and Products when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      if (currentBranch) {
        setSelectedBranch(currentBranch);
      } else if (branches.length > 0 && !selectedBranch) {
        setSelectedBranch(branches[0]._id || branches[0].id || '');
      }
      setWastageList([]);
      resetDraftForm();
    }
  }, [isOpen, currentBranch]);

  // Load branch stock levels whenever selectedBranch changes
  useEffect(() => {
    if (!selectedBranch || !isOpen) return;

    const fetchBranchStocks = async () => {
      try {
        const [prodInvRes, rmInvRes] = await Promise.all([
          ProductInventoryService.getAllInventories({ branchId: selectedBranch }).catch(() => ({})),
          RawMaterialInventoryService.getAllInventories({ branchId: selectedBranch }).catch(() => ({})),
        ]);

        const prodInvs = prodInvRes?.data?.productInventories || prodInvRes?.productInventories || prodInvRes?.data || [];
        const rmInvs = rmInvRes?.data?.rawMaterialInventories || rmInvRes?.rawMaterialInventories || rmInvRes?.data || [];

        const pMap = {};
        if (Array.isArray(prodInvs)) {
          prodInvs.forEach((inv) => {
            const pId = inv.product?._id || inv.product;
            if (pId) pMap[String(pId)] = inv.currentStock ?? 0;
          });
        }
        setBranchProductStockMap(pMap);

        const rmMap = {};
        if (Array.isArray(rmInvs)) {
          rmInvs.forEach((inv) => {
            const rmId = inv.rawMaterial?._id || inv.rawMaterial;
            if (rmId) rmMap[String(rmId)] = inv.currentStock ?? 0;
          });
        }
        setBranchRawMaterialStockMap(rmMap);
      } catch (err) {
        console.warn('Could not fetch branch stock for wastage modal:', err);
      }
    };

    fetchBranchStocks();
  }, [selectedBranch, isOpen]);

  const loadInitialData = async () => {
    try {
      setFetchingItems(true);
      const [rmRes, prodRes] = await Promise.all([
        rawMaterialsService.getAllRawMaterials({ limit: 500 }),
        ProductService.getAllProducts({ limit: 500 }),
      ]);

      const rmData = rmRes?.data?.rawMaterials || rmRes?.rawMaterials || rmRes?.data || [];
      const prodData = prodRes?.data?.products || prodRes?.products || prodRes?.data || [];

      setRawMaterials(Array.isArray(rmData) ? rmData : []);
      setProducts(Array.isArray(prodData) ? prodData : []);
    } catch (err) {
      console.error('Failed to load items for wastage entry:', err);
      toast.error('Failed to load raw materials and products');
    } finally {
      setFetchingItems(false);
    }
  };

  const resetDraftForm = () => {
    setSelectedItemId('');
    setQuantity('');
    setUnitName('units');
    setReason('trimming_prep');
    setNotes('');
  };

  // Options for SearchableSelect with live stock
  const rawMaterialOptions = useMemo(() => {
    return rawMaterials.map((rm) => {
      const stock = branchRawMaterialStockMap[String(rm._id)];
      const stockStr = stock !== undefined ? `Stock: ${stock} ${rm.unit?.abbreviation || rm.unit?.name || 'units'}` : '';
      return {
        value: rm._id,
        label: rm.name,
        subtext: `${rm.code || 'Code N/A'} • ${stockStr ? stockStr + ' • ' : ''}Price: Rs. ${(rm.unitPrice || 0).toFixed(2)}`
      };
    });
  }, [rawMaterials, branchRawMaterialStockMap]);

  const productOptions = useMemo(() => {
    return products.map((p) => {
      const stock = branchProductStockMap[String(p._id)];
      const isRecipe = p.type === 'recipe_based';
      const stockStr = isRecipe ? 'Recipe-Based (Deducts Ingredients)' : (stock !== undefined ? `Stock: ${stock} pcs` : '');
      return {
        value: p._id,
        label: p.name,
        subtext: `SKU: ${p.sku || 'N/A'} • ${stockStr ? stockStr + ' • ' : ''}Cost: Rs. ${(p.cost || p.outletPrice || 0).toFixed(2)}`
      };
    });
  }, [products, branchProductStockMap]);

  // Update unit name when item selection changes
  useEffect(() => {
    if (itemType === 'raw_material' && selectedItemId) {
      const item = rawMaterials.find((r) => String(r._id) === String(selectedItemId));
      if (item) {
        setUnitName(item.unit?.abbreviation || item.unit?.name || 'unit');
      }
    } else if (itemType === 'product' && selectedItemId) {
      setUnitName('pcs');
    }
  }, [selectedItemId, itemType, rawMaterials]);

  if (!isOpen) return null;

  // Add Current Draft Item to the Queue List
  const handleAddItem = () => {
    if (!selectedItemId) {
      toast.error(itemType === 'raw_material' ? 'Please select a raw material' : 'Please select a product');
      return;
    }

    if (!quantity || Number(quantity) <= 0) {
      toast.error('Please enter a valid wastage quantity');
      return;
    }

    let itemObj = null;
    let unitCost = 0;
    let itemName = '';

    if (itemType === 'raw_material') {
      itemObj = rawMaterials.find((rm) => String(rm._id) === String(selectedItemId));
      unitCost = itemObj?.unitPrice || 0;
      itemName = itemObj?.name || 'Raw Material';
    } else {
      itemObj = products.find((p) => String(p._id) === String(selectedItemId));
      unitCost = itemObj?.cost || itemObj?.outletPrice || 0;
      itemName = itemObj?.name || 'Product';
    }

    const newItem = {
      id: `wastage-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      itemType,
      rawMaterialId: itemType === 'raw_material' ? selectedItemId : null,
      productId: itemType === 'product' ? selectedItemId : null,
      itemName,
      quantity: Number(quantity),
      unitName,
      unitCost,
      totalCost: Math.round(Number(quantity) * unitCost * 100) / 100,
      reason,
      notes: notes || ''
    };

    setWastageList((prev) => [...prev, newItem]);
    resetDraftForm();
    toast.success(`Added ${itemName} to wastage queue!`);
  };

  // Remove Item from Queue List
  const handleRemoveItem = (id) => {
    setWastageList((prev) => prev.filter((item) => item.id !== id));
  };

  // Inline update item quantity in Queue List
  const handleUpdateItemQuantity = (id, newQty) => {
    setWastageList((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const qty = Math.max(0, Number(newQty) || 0);
          return {
            ...item,
            quantity: qty,
            totalCost: Math.round(qty * item.unitCost * 100) / 100
          };
        }
        return item;
      })
    );
  };

  // Submit all queued wastage entries
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }

    let itemsToSubmit = [...wastageList];

    // If queue is empty but draft fields are filled, auto-add draft item
    if (itemsToSubmit.length === 0) {
      if (selectedItemId && quantity && Number(quantity) > 0) {
        let itemObj = null;
        let unitCost = 0;
        let itemName = '';

        if (itemType === 'raw_material') {
          itemObj = rawMaterials.find((rm) => String(rm._id) === String(selectedItemId));
          unitCost = itemObj?.unitPrice || 0;
          itemName = itemObj?.name || 'Raw Material';
        } else {
          itemObj = products.find((p) => String(p._id) === String(selectedItemId));
          unitCost = itemObj?.cost || itemObj?.outletPrice || 0;
          itemName = itemObj?.name || 'Product';
        }

        itemsToSubmit.push({
          itemType,
          rawMaterialId: itemType === 'raw_material' ? selectedItemId : null,
          productId: itemType === 'product' ? selectedItemId : null,
          itemName,
          quantity: Number(quantity),
          unitName,
          unitCost,
          totalCost: Math.round(Number(quantity) * unitCost * 100) / 100,
          reason,
          notes: notes || ''
        });
      } else {
        toast.error('Please add at least one item to the wastage list');
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        branchId: selectedBranch,
        items: itemsToSubmit.map((item) => ({
          itemType: item.itemType,
          rawMaterialId: item.rawMaterialId,
          productId: item.productId,
          branchId: selectedBranch,
          quantity: item.quantity,
          unitName: item.unitName,
          reason: item.reason,
          notes: item.notes
        }))
      };

      await analyticsService.createWastageEntry(payload);
      toast.success(`Successfully logged ${itemsToSubmit.length} wastage entries & updated stock!`);

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting wastage logs:', err);
      toast.error(err.message || 'Failed to submit wastage logs');
    } finally {
      setLoading(false);
    }
  };

  const totalBatchCost = wastageList.reduce((sum, item) => sum + (item.totalCost || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200 my-8 flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-orange-600 p-5 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Trash2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold tracking-tight">Bulk Manual Wastage Entry</h2>
              <p className="text-xs text-rose-100">Add multiple raw materials & finished products to bulk log wastage</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Branch Select Bar */}
          <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <label className="block text-xs font-extrabold text-rose-900 uppercase tracking-wider mb-0.5">
                Target Branch *
              </label>
              <p className="text-xs text-rose-700">Stock will be automatically deducted from this branch inventory</p>
            </div>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              required
              className="w-full sm:w-64 px-3 py-2 border border-rose-300 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 bg-white shadow-sm"
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b._id || b.id} value={b._id || b.id}>
                  📍 {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Add Item Form Bar */}
          <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                <Plus className="h-4 w-4 text-rose-600" />
                Add Item to Wastage Queue
              </span>

              {/* Category Switcher */}
              <div className="flex items-center bg-gray-200 p-0.5 rounded-lg text-xs font-bold">
                <button
                  type="button"
                  onClick={() => {
                    setItemType('raw_material');
                    setSelectedItemId('');
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all ${
                    itemType === 'raw_material' ? 'bg-white text-rose-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📦 Raw Material
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setItemType('product');
                    setSelectedItemId('');
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all ${
                    itemType === 'product' ? 'bg-white text-rose-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  🍰 Finished Product
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              {/* Item Selector Dropdown (Searchable) */}
              <div className="md:col-span-5">
                <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                  Select {itemType === 'raw_material' ? 'Raw Material' : 'Finished Product'} *
                </label>
                <SearchableSelect
                  options={itemType === 'raw_material' ? rawMaterialOptions : productOptions}
                  value={selectedItemId}
                  onChange={(val) => setSelectedItemId(val)}
                  placeholder={`-- Search ${itemType === 'raw_material' ? 'Raw Material' : 'Product'} --`}
                  searchPlaceholder="Type to filter items..."
                  disabled={fetchingItems}
                />
                {selectedItemId && (
                  <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                    <span className="text-gray-500 font-medium">Branch Stock:</span>
                    {itemType === 'raw_material' ? (
                      <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                        (branchRawMaterialStockMap[String(selectedItemId)] || 0) > 0
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}>
                        {branchRawMaterialStockMap[String(selectedItemId)] ?? 0} {unitName}
                      </span>
                    ) : (
                      (() => {
                        const selProd = products.find((p) => String(p._id) === String(selectedItemId));
                        if (selProd?.type === 'recipe_based') {
                          return (
                            <span className="font-bold px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">
                              🍳 Recipe-Based (Ingredients deducted)
                            </span>
                          );
                        }
                        const st = branchProductStockMap[String(selectedItemId)];
                        return (
                          <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                            (st || 0) > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                          }`}>
                            {st ?? 0} pcs
                          </span>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div className="md:col-span-3">
                <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                  Wastage Qty * ({unitName})
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="e.g. 2.5"
                    className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-rose-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Reason */}
              <div className="md:col-span-4">
                <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                  Wastage Reason
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg text-xs font-semibold focus:ring-2 focus:ring-rose-500 bg-white"
                >
                  {WASTAGE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes / Remarks Row & Add Button */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional remarks (e.g. 500g bone waste from chicken delivery)..."
                className="flex-1 h-9 px-3 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-rose-500"
              />

              <button
                type="button"
                onClick={handleAddItem}
                className="w-full sm:w-auto px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all"
              >
                <Plus className="h-4 w-4" />
                <span>Add to List</span>
              </button>
            </div>
          </div>

          {/* Multi-Item Queue Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-sm text-gray-900 flex items-center gap-2">
                <Layers className="h-4 w-4 text-rose-600" />
                Queued Wastage Items ({wastageList.length})
              </h3>
              {totalBatchCost > 0 && (
                <span className="text-xs font-extrabold text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200">
                  Est. Batch Loss: Rs. {totalBatchCost.toFixed(2)}
                </span>
              )}
            </div>

            {wastageList.length === 0 ? (
              <div className="p-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <Package className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-xs font-bold text-gray-600">No items added to the wastage queue yet</p>
                <p className="text-[11px] text-gray-400 mt-1">Select raw materials or products above and click "+ Add to List"</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 font-bold uppercase border-b border-gray-200">
                      <th className="py-2.5 px-3">#</th>
                      <th className="py-2.5 px-3">Category</th>
                      <th className="py-2.5 px-3">Item Name</th>
                      <th className="py-2.5 px-3 text-center">Wastage Qty</th>
                      <th className="py-2.5 px-3">Reason</th>
                      <th className="py-2.5 px-3 text-right">Est. Cost</th>
                      <th className="py-2.5 px-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {wastageList.map((item, index) => (
                      <tr key={item.id} className="hover:bg-rose-50/20 transition-colors">
                        <td className="py-2.5 px-3 text-gray-400 font-semibold">{index + 1}</td>

                        <td className="py-2.5 px-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-extrabold uppercase ${
                              item.itemType === 'raw_material'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {item.itemType === 'raw_material' ? 'Raw Material' : 'Finished Product'}
                          </span>
                        </td>

                        <td className="py-2.5 px-3 font-bold text-gray-900">{item.itemName}</td>

                        <td className="py-2.5 px-3 text-center">
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItemQuantity(item.id, e.target.value)}
                              className="w-16 h-7 px-1.5 border border-gray-300 rounded text-center font-bold text-gray-900 focus:ring-1 focus:ring-rose-500"
                            />
                            <span className="text-[10px] text-gray-500 font-semibold">{item.unitName}</span>
                          </div>
                        </td>

                        <td className="py-2.5 px-3 text-gray-600">
                          {WASTAGE_REASONS.find((r) => r.value === item.reason)?.label.split('(')[0] || item.reason}
                        </td>

                        <td className="py-2.5 px-3 text-right font-extrabold text-rose-700">
                          Rs. {item.totalCost.toFixed(2)}
                        </td>

                        <td className="py-2.5 px-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
          <div className="text-xs text-gray-500">
            <span className="font-semibold text-gray-700">Note:</span> Submitting will automatically deduct stock from branch inventory.
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-initial px-4 py-2 bg-white border border-gray-300 hover:bg-gray-100 rounded-xl font-bold text-xs text-gray-700 shadow-sm transition-all"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || (wastageList.length === 0 && (!selectedItemId || !quantity))}
              className="flex-1 sm:flex-initial px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-extrabold text-xs shadow-md transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span>Log {wastageList.length > 0 ? `${wastageList.length} Items` : 'Wastage'} & Deduct Stock</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWastageModal;
